import { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    allowPerformAction,
    formatDesignationDisplay,
    getDomainValueForCode,
} from '../../../../library/Core/SmartFunctions';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { pickDisplayJob } from '../../../../library/Service/currentJobDisplay';
import { getInitials } from '../../../../library/SimpleControls/InitialsAvatar';
import { Tooltip } from 'bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const END_DATE_TIP = "You selected I've left this role. Enter the month you left. Saving it marks this job as no longer current.";

// userapi sends DATE columns as a UTC instant (2024-07-01 IST -> 2024-06-30T18:30Z), so read them in local
// time like the work form does; a bare yyyy-mm-dd is taken as written.
const toLocalDate = (value: unknown): Date | null => {
    if (!value) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const date = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value as string);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Month/year only, saved as the 15th like YearAndMonthSelector on the work form. In the start month,
// a start day after the 15th is used so close-current does not reject it as before the start date.
const toMonthEndDate = (date: Date, start: Date | null) => {
    const day =
        start && start.getFullYear() === date.getFullYear() && start.getMonth() === date.getMonth()
            ? Math.max(15, start.getDate())
            : 15;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const reviewedWithinTwoYears = (value: unknown) => {
    if (!value) return false;
    const reviewed = new Date(value as string);
    if (Number.isNaN(reviewed.getTime())) return false;
    const expires = new Date(reviewed);
    expires.setFullYear(expires.getFullYear() + 2);
    return expires.getTime() > Date.now();
};

const reviewStillCoversJobs = (jobs: any[], reviewedAt: unknown) => {
    const reviewed = new Date(reviewedAt as string).getTime();
    if (Number.isNaN(reviewed) || !jobs.length) return false;
    return jobs.some((job) => {
        const created = new Date(job?.createdAt ?? job?.created_at).getTime();
        if (Number.isNaN(created)) return true;
        return created <= reviewed;
    });
};

const mergeSavedJobs = (data: any, saved: any) => {
    const savedJobs = Array.isArray(saved?.jobs) ? saved.jobs : [];
    const byId = new Map<string, any>(savedJobs.map((job: any) => [String(job.id), job]));
    const patch = (job: any) => {
        const next = byId.get(String(job?.id));
        if (!next) return job;
        return { ...job, isCurrent: next.isCurrent, isPrimaryCurrent: next.isPrimaryCurrent, endDate: next.endDate };
    };
    const patchList = (list: any) => (Array.isArray(list) ? list.map(patch) : list);
    return {
        ...data,
        jobs: patchList(data?.jobs),
        jobsList: patchList(data?.jobsList),
        profile: {
            ...data?.profile,
            primaryJobReviewedAt: saved?.profile?.primaryJobReviewedAt ?? null,
        },
    };
};

const flagOn = (value: any) => value === true || value === 1 || value === '1';

const isCurrentJob = (job: any) => {
    const recordType = job?.recordTypeCode ?? job?.record_type_code;
    return flagOn(job?.isCurrent ?? job?.is_current) && (recordType == null || recordType === 'JOINED');
};

const isPrimaryJob = (job: any) => flagOn(job?.isPrimaryCurrent ?? job?.is_primary_current ?? job?.isPrimary);

const jobEditPath = (studentId: string, job: any) => {
    const paths: Record<string, string> = {
        TEACHING_AND_RESEARCH: 'teaching-experience',
        PROFESSIONAL_PRACTICE: 'professional-practice-experience',
        DEFENCE: 'defence-experience',
        IN_DEFENCE: 'defence-experience',
        ENTREPRENEURSHIP: 'entrepreneurship',
        SELF_EMPLOYED_AND_FREELANCING: 'freelancing-experience',
    };
    const segment = paths[job?.jobCategory] || 'work';
    return `/alumni/${studentId}/${job?.id}/${segment}`;
};

const jobTitle = (job: any, state: any) => {
    const category = job?.jobCategory;
    if (category === 'TEACHING_AND_RESEARCH') {
        if (job?.instituteId == null || job?.instituteId === '' || Number(job?.instituteId) === 0) {
            return job?.otherInstitute || '';
        }
        const name = getDomainValueForCode(job.instituteId, 'INSTITUTE_DOMAIN', state);
        return name && String(name) !== String(job.instituteId) ? name : job?.otherInstitute || name;
    }
    if (job?.companyId == null || job?.companyId === '' || Number(job?.companyId) === 0) {
        return job?.otherCompany || '';
    }
    const name = getDomainValueForCode(job.companyId, 'COMPANY_CODE', state);
    return name && String(name) !== String(job.companyId) ? name : job?.otherCompany || name;
};

const sinceLine = (job: any, state: any) => {
    const parts: string[] = [];
    const start = toLocalDate(job?.startDate);
    if (start) {
        parts.push(`Since ${start.toLocaleString('en-US', { month: 'short' })} ${start.getFullYear()}`);
    }
    const city = job?.cityCode ? getDomainValueForCode(job.cityCode, 'CITY_CODE', state) : '';
    if (city && String(city) !== String(job?.cityCode)) parts.push(city);
    return parts.join(' · ');
};

const CurrentRolesReviewNotice = () => {
    const { state, dispatch } = useContext(SmartContext);
    const navigate = useNavigate();
    const studentId = state?.data?.id;
    const currentJobs = useMemo(() => {
        const jobs = state?.data?.jobsList ?? state?.data?.jobs ?? [];
        return (Array.isArray(jobs) ? jobs : []).filter(isCurrentJob);
    }, [state?.data?.jobsList, state?.data?.jobs]);
    const primaryId = currentJobs.find(isPrimaryJob)?.id ?? 'none';
    const sessionKey = studentId
        ? `primaryJobReviewDismissed:${studentId}:${primaryId}:${state?.data?.profile?.primaryJobReviewedAt ?? ''}`
        : '';
    const [dismissed, setDismissed] = useState(() => (sessionKey ? sessionStorage.getItem(sessionKey) === '1' : false));
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [leavingJobId, setLeavingJobId] = useState<string | number | null>(null);
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        setDismissed(sessionKey ? sessionStorage.getItem(sessionKey) === '1' : false);
    }, [sessionKey]);

    useEffect(() => {
        if (!open || leavingJobId == null) return;
        const nodes = Array.from(document.querySelectorAll('[data-end-date-tip="1"]'));
        const tips = nodes.map(
            (node) =>
                new Tooltip(node, {
                    container: 'body',
                    trigger: 'hover focus',
                    title: END_DATE_TIP,
                    customClass: 'end-date-tip',
                })
        );
        tips.forEach((tip) => tip.show());
        return () => tips.forEach((tip) => tip.dispose());
    }, [open, leavingJobId]);

    const workingStatusCode = state?.data?.profile?.workingStatusCode;
    const hasPrimary = currentJobs.some(isPrimaryJob);
    const reviewedAt = state?.data?.profile?.primaryJobReviewedAt;
    const reviewIsCurrent = reviewedWithinTwoYears(reviewedAt) && reviewStillCoversJobs(currentJobs, reviewedAt);
    const { job: suggested } = pickDisplayJob(currentJobs, undefined, workingStatusCode);
    const savedPrimary = currentJobs.find(isPrimaryJob) || suggested;

    if (currentJobs.length < 2 || dismissed) return null;
    if (hasPrimary && reviewIsCurrent) return null;

    const chosenId = selectedId ?? savedPrimary?.id;
    const count = currentJobs.length;
    const readOnly = !allowPerformAction();
    const readOnlyTitle = 'You can only view this profile in impersonation mode.';

    const hideForSession = () => {
        if (sessionKey) sessionStorage.setItem(sessionKey, '1');
        setDismissed(true);
        setOpen(false);
    };

    const selectRole = (jobId: string | number) => {
        if (readOnly) return;
        setSelectedId(jobId);
        setLeavingJobId(null);
        setEndDate('');
        setError('');
    };

    const confirmPrimary = async () => {
        if (readOnly || !studentId || chosenId == null || saving || leavingJobId != null) return;
        setSaving(true);
        setError('');
        try {
            const { data } = await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/profile/primary-job-review`, {
                student_id: studentId,
                job_id: Number(chosenId),
            });
            if (sessionKey) sessionStorage.removeItem(sessionKey);
            dispatch({ type: 'REFRESH_DATA', payload: { data: mergeSavedJobs(state.data, data) } });
            setOpen(false);
        } catch {
            setError('Could not save the primary role. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const leaveRole = async (job: any) => {
        if (readOnly || !studentId || saving) return;
        if (!endDate) {
            setError('Enter the end date for this role.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const { data } = await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/profile/close-current`, {
                student_id: studentId,
                job_id: Number(job.id),
                end_date: endDate,
            });
            if (String(selectedId) === String(job.id)) setSelectedId(null);
            setLeavingJobId(null);
            setEndDate('');
            dispatch({ type: 'REFRESH_DATA', payload: { data: mergeSavedJobs(state.data, data) } });
        } catch {
            setError('Could not close this role. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const logoColors = ['#b02324', '#1e3a8a', '#ea580c', '#5c6bc0', '#0f766e'];

    return (
        <div className="container-fluid bg-white">
            <div className="max-1140 mx-auto pb-4">
                <div
                    className="alert alert-dismissible fade show d-flex flex-wrap align-items-center justify-content-between px-3 px-md-4 py-3 mb-0 border-0 rounded-3 gap-3"
                    role="alert"
                    style={{ backgroundColor: '#eaf4fa' }}>
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-circle bg-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                            style={{ width: 44, height: 44 }}>
                            <i className="bi bi-briefcase" style={{ fontSize: '1.15rem', color: '#1976d2' }} />
                        </div>
                        <div className="text-dark d-flex flex-column font-14 lh-base">
                            <strong className="fw-bold text-dark">You're listed as currently working at {count} organisations.</strong>
                            <span style={{ color: '#495057' }}>
                                Choose which one appears first on your profile, and close any that have ended.
                            </span>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-shrink-0 ms-auto ms-md-0">
                        <button
                            type="button"
                            className="btn btn-success rounded-pill px-4 py-2 text-white fw-semibold border-0 shadow-none"
                            onClick={() => setOpen(true)}>
                            Review now
                        </button>
                        <button
                            type="button"
                            className="btn-close shadow-none p-2 m-0 position-static"
                            aria-label="Close"
                            style={{ fontSize: '0.8rem' }}
                            onClick={hideForSession}
                        />
                    </div>
                </div>
            </div>

            {open &&
                createPortal(
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                        style={{ zIndex: 10600, background: 'rgba(16, 24, 40, 0.45)' }}>
                        <style>{`
                            .role-selection-card:hover { border-color: #cbd5e1 !important; }
                            .role-selection-card.is-selected { border-color: #1b7a43 !important; }
                            .role-selection-card.is-selected:hover { border-color: #1b7a43 !important; }
                            .btn-role-action:hover { background-color: #f8fafc !important; border-color: #9ca3af !important; color: #111827 !important; }
                            .tooltip.end-date-tip { z-index: 10850; }
                            .end-date-picker-popper { z-index: 10900; }
                        `}</style>
                        <div
                            className="bg-white border-0 shadow-sm p-3 p-md-4"
                            role="dialog"
                            aria-labelledby="review-current-roles-title"
                            style={{ width: 'min(820px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', borderRadius: 16 }}>
                            <div className="d-flex align-items-start justify-content-between gap-3 px-2 px-md-3 pt-1">
                                <div className="pe-3">
                                    <h4 id="review-current-roles-title" className="fw-bold text-dark mb-2" style={{ fontSize: 24, letterSpacing: '-0.3px', color: '#111827' }}>
                                        Review your current roles
                                    </h4>
                                    <p className="mb-0" style={{ fontSize: 14.5, lineHeight: 1.5, color: '#4b5563' }}>
                                        These roles are marked current on your profile. Pick the one to show first on your directory card and profile, and fix or close anything that's out of date.
                                    </p>
                                </div>
                                <button type="button" className="btn-close flex-shrink-0 shadow-none" aria-label="Close" style={{ opacity: 0.65, fontSize: 13 }} onClick={() => setOpen(false)} />
                            </div>
                            <div className="px-2 px-md-3 pt-3 pb-2">
                                <div className="fw-bold text-uppercase mb-3" style={{ fontSize: 12, letterSpacing: '0.6px', color: '#1b7a43' }}>
                                    SHOW FIRST ON MY PROFILE
                                </div>
                                <div className="d-flex flex-column gap-3">
                                    {currentJobs.map((job: any, index: number) => {
                                        const title = jobTitle(job, state);
                                        const selected = String(job.id) === String(chosenId);
                                        const designation = formatDesignationDisplay(job?.designation, state, {
                                            workingStatusCode: job?.jobCategory,
                                        });
                                        return (
                                            <div
                                                key={job.id}
                                                className={`role-selection-card p-3 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 ${selected ? 'is-selected' : ''}`}
                                                onClick={() => selectRole(job.id)}
                                                style={{
                                                    border: selected ? '1.5px solid #1b7a43' : '1px solid #e5e7eb',
                                                    backgroundColor: '#fff',
                                                    cursor: readOnly ? 'default' : 'pointer',
                                                    borderRadius: 12,
                                                }}>
                                                <div className="d-flex align-items-start align-items-sm-center gap-3">
                                                    <div className="d-flex align-items-center justify-content-center flex-shrink-0 pt-1 pt-sm-0">
                                                        <span
                                                            className="d-inline-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                border: selected ? '2px solid #1b7a43' : '1.5px solid #9ca3af',
                                                                background: '#fff',
                                                            }}>
                                                            <span
                                                                style={{
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#1b7a43',
                                                                    display: selected ? 'block' : 'none',
                                                                }}
                                                            />
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                                        style={{ width: 48, height: 48, backgroundColor: logoColors[index % logoColors.length], fontSize: 15, borderRadius: 8 }}>
                                                        {getInitials(undefined, undefined, title)}
                                                    </div>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                            <span className="fw-bold" style={{ fontSize: 16, color: '#111827' }}>{title}</span>
                                                            {suggested && String(suggested.id) === String(job.id) && (
                                                                <span className="badge rounded-pill fw-medium" style={{ backgroundColor: '#e0f2fe', color: '#0284c7', fontSize: 11, padding: '3px 9px' }}>
                                                                    Suggested
                                                                </span>
                                                            )}
                                                            {selected && (
                                                                <span className="badge rounded-pill fw-medium" style={{ backgroundColor: '#e8f5e9', color: '#1b7a43', fontSize: 11, padding: '3px 9px' }}>
                                                                    Primary
                                                                </span>
                                                            )}
                                                        </div>
                                                        {designation && (
                                                            <div className="fw-medium mb-1" style={{ color: '#0284c7', fontSize: 14 }}>
                                                                {designation}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: 13, color: '#64748b' }}>{sinceLine(job, state)}</div>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto ps-sm-2" onClick={(event) => event.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-role-action bg-white"
                                                        style={{ border: '1px solid #d1d5db', color: '#1f2937', borderRadius: 8, fontWeight: 500, fontSize: 13.5, padding: '6px 16px', opacity: readOnly ? 0.55 : 1 }}
                                                        disabled={readOnly}
                                                        title={readOnly ? readOnlyTitle : undefined}
                                                        onClick={() => {
                                                            if (readOnly) return;
                                                            navigate(jobEditPath(studentId, job));
                                                        }}>
                                                        Edit
                                                    </button>
                                                    {String(leavingJobId) === String(job.id) ? (
                                                        <>
                                                            <span
                                                                className="d-inline-block"
                                                                style={{ width: 150 }}
                                                                data-end-date-tip="1"
                                                                data-bs-toggle="tooltip"
                                                                data-bs-placement="top"
                                                                data-bs-title={END_DATE_TIP}
                                                                title={END_DATE_TIP}>
                                                                <DatePicker
                                                                    className="form-control form-control-sm"
                                                                    selected={toLocalDate(endDate)}
                                                                    onChange={(date: Date | null) => setEndDate(date ? toMonthEndDate(date, toLocalDate(job?.startDate)) : '')}
                                                                    showMonthYearPicker
                                                                    dateFormat="MMM, yyyy"
                                                                    placeholderText="Month, Year"
                                                                    minDate={toLocalDate(job?.startDate)}
                                                                    maxDate={new Date()}
                                                                    popperClassName="end-date-picker-popper"
                                                                    portalId="root"
                                                                />
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-role-action bg-white"
                                                                style={{ border: '1px solid #d1d5db', color: '#1f2937', borderRadius: 8, fontWeight: 500, fontSize: 13.5, padding: '6px 16px', opacity: readOnly ? 0.55 : 1 }}
                                                                disabled={saving || !endDate || readOnly}
                                                                title={readOnly ? readOnlyTitle : undefined}
                                                                onClick={() => leaveRole(job)}>
                                                                Save end date
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-role-action bg-white"
                                                            style={{ border: '1px solid #d1d5db', color: '#1f2937', borderRadius: 8, fontWeight: 500, fontSize: 13.5, padding: '6px 16px', opacity: readOnly ? 0.55 : 1 }}
                                                            disabled={saving || readOnly}
                                                            title={readOnly ? readOnlyTitle : undefined}
                                                            onClick={() => {
                                                                if (readOnly) return;
                                                                setLeavingJobId(job.id);
                                                                setEndDate('');
                                                                setError('');
                                                            }}>
                                                            I've left this role
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {error && <p className="text-danger mb-0 mt-3" style={{ fontSize: 13.5 }}>{error}</p>}
                            </div>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 px-2 px-md-3 pt-3 pb-1">
                                <div style={{ fontSize: 13.5, lineHeight: 1.4, maxWidth: 330, color: '#4b5563' }}>
                                    Your primary role is shown on your directory card and at the top of your profile.
                                </div>
                                <div className="d-flex align-items-center gap-3 ms-auto">
                                    <button
                                        type="button"
                                        className="btn btn-link text-decoration-none fw-medium px-2 py-1 shadow-none"
                                        style={{ fontSize: 14.5, color: '#374151' }}
                                        onClick={hideForSession}
                                        disabled={saving}>
                                        Remind me later
                                    </button>
                                    <button
                                        type="button"
                                        className="btn rounded-pill text-white fw-semibold px-4 py-2 shadow-none"
                                        style={{ backgroundColor: '#2e7d32', fontSize: 15, border: 'none', opacity: readOnly || leavingJobId != null ? 0.55 : 1 }}
                                        onClick={confirmPrimary}
                                        disabled={saving || leavingJobId != null || readOnly}
                                        title={readOnly ? readOnlyTitle : leavingJobId != null ? 'Save the end date before confirming your current roles.' : undefined}>
                                        Yes, this is right
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default CurrentRolesReviewNotice;
